import React, { FC, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { Button, Modal } from '@clearcover/atlas';
import { ButtonGroup } from 'Shared/components';
import { formatPhoneNumber } from 'Shared/utils';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  Address,
  ContactInfo,
  MailingAddress,
  SaveDiscardModal,
} from 'QuoteApp/components';
import {
  ContactInfoType,
  FormValuesType,
  GetDetailsResponseType,
} from 'QuoteApp/services/endpoints/GetDetails';
import { useAppContext } from 'QuoteApp/services/providers';
import {
  getContactInfoCompleteness,
  formatAddressDisplay,
  formatMailingAddressDisplay,
} from 'QuoteApp/utils';
import { useEdit, useFieldData } from 'QuoteApp/utils/contexts';

type PolicyContactInfoPropTypes = {
  addressEditingId: string;
  mailingAddressEditingId: string;
  contactInfoEditingId: string;
  editingIdDisplay: string;
};

const formatPhoneAndEmailAccordionHeader = (
  contactInfo: ContactInfoType
): string => {
  if (contactInfo) {
    return `Phone: ${formatPhoneNumber(
      contactInfo.phoneNumber.value
    )} | Email: ${contactInfo.email.value}`;
  }
  return `Phone & Email Address`;
};

export const PolicyContactInfo: FC<PolicyContactInfoPropTypes> = ({
  addressEditingId,
  mailingAddressEditingId,
  contactInfoEditingId,
  editingIdDisplay,
}) => {
  const { details, config } = useAppContext();
  const {
    isSubmitting,
    isValid,
    submitForm,
    resetForm,
    dirty,
  } = useFormikContext<FormValuesType>();

  const { serverErrors } = useFieldData();
  const { editingId, changeEditingId } = useEdit();

  const isAccessEnabled = !!config?.accessEnabled;

  const addressComplete = (
    addressValid: GetDetailsResponseType['addressOutcomes']
  ) => {
    return addressValid.isAddressValid && !addressValid.isMissingLineTwo;
  };

  const mailingAddressComplete = (
    mailingAddressOutcomes: GetDetailsResponseType['mailingAddressOutcomes']
  ) => {
    return (
      mailingAddressOutcomes?.isAddressValid &&
      !mailingAddressOutcomes.isMissingLineTwo
    );
  };

  const phoneAndEmailDataCompleteness = useMemo(
    () => getContactInfoCompleteness(serverErrors.contactInfo),
    [serverErrors.contactInfo]
  );

  const isEmailEditable =
    details.drivers[details.principalInsuredId].email.editable;

  const isMailingAddressEditable =
    details.drivers[details.principalInsuredId].editable &&
    editingId === mailingAddressEditingId;

  return (
    <div data-testid="policy-contact-info">
      <Accordion>
        <AccordionItem
          identifier="addressAccordion"
          key={addressEditingId}
          header={
            <AccordionHeader
              entity="marker"
              header={
                (isAccessEnabled ? 'Garaging Address: ' : '') +
                formatAddressDisplay(
                  details.address[details.principalInsuredId]
                )
              }
              dataComplete={addressComplete(details.addressOutcomes)}
            />
          }
          startOpen={editingId === addressEditingId}
          actions={setIsOpen =>
            editingId === addressEditingId ? (
              <Button
                variant="secondary"
                size="sm"
                type="submit"
                onClick={async () => {
                  await submitForm();
                  changeEditingId(null);
                }}
                isSubmitting={isSubmitting}
                disabled={isSubmitting || !isValid}
                data-testid={`save-${details.principalInsuredId}-address`}
              >
                Save
              </Button>
            ) : dirty ? (
              <Modal
                variant="normal"
                toggle={openModal => (
                  <Button
                    data-testid={`edit-${details.principalInsuredId}-address`}
                    variant="secondary"
                    size="sm"
                    onClick={openModal}
                  >
                    Edit
                  </Button>
                )}
              >
                {() => (
                  <SaveDiscardModal
                    onSave={async () => {
                      await submitForm();
                      setIsOpen(true);
                      changeEditingId(addressEditingId);
                    }}
                    itemDisplay={editingIdDisplay}
                    saveSubmitting={isSubmitting}
                    onDiscard={() => {
                      resetForm();
                      changeEditingId(addressEditingId);
                      setIsOpen(true);
                    }}
                  />
                )}
              </Modal>
            ) : (
              <Button
                data-testid={`edit-${details.principalInsuredId}-address`}
                variant="secondary"
                size="sm"
                onClick={() => {
                  changeEditingId(addressEditingId);
                  setIsOpen(true);
                }}
              >
                Edit
              </Button>
            )
          }
          editable={details.drivers[details.principalInsuredId].editable}
          footer={
            editingId === addressEditingId && (
              <>
                <ButtonGroup>
                  <Button
                    data-testid={`cancel-contact-info-footer`}
                    variant="secondary"
                    disabled={isSubmitting}
                    onClick={() => {
                      resetForm();
                      changeEditingId(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    isSubmitting={isSubmitting}
                    disabled={isSubmitting || !isValid}
                    data-testid={'save-contact-info-footer'}
                    onClick={async () => {
                      await submitForm();
                      changeEditingId(null);
                    }}
                  >
                    Save Address
                  </Button>
                </ButtonGroup>
              </>
            )
          }
        >
          <Address
            editable={
              details.drivers[details.principalInsuredId].editable &&
              editingId === addressEditingId
            }
            allowStateChange={false}
            tooltip={isAccessEnabled ? '' : undefined}
          />
        </AccordionItem>
        {isAccessEnabled && (
          <AccordionItem
            identifier="mailingAddressAccordion"
            key={mailingAddressEditingId}
            startOpen={editingId === mailingAddressEditingId}
            header={
              <AccordionHeader
                entity="marker"
                header={`Mailing Address: ${formatMailingAddressDisplay(
                  details.mailingAddress[details.principalInsuredId]
                )}`}
                dataComplete={mailingAddressComplete(
                  details.mailingAddressOutcomes
                )}
              />
            }
            actions={setIsOpen =>
              editingId === mailingAddressEditingId ? (
                <Button
                  variant="secondary"
                  size="sm"
                  type="submit"
                  onClick={async () => {
                    await submitForm();
                    changeEditingId(null);
                  }}
                  isSubmitting={isSubmitting}
                  disabled={isSubmitting || !isValid}
                  data-testid={`save-${details.principalInsuredId}-mailing-address`}
                >
                  Save
                </Button>
              ) : dirty ? (
                <Modal
                  variant="normal"
                  toggle={openModal => (
                    <Button
                      data-testid={`edit-${details.principalInsuredId}-mailing-address`}
                      variant="secondary"
                      size="sm"
                      onClick={openModal}
                    >
                      Edit
                    </Button>
                  )}
                >
                  {() => (
                    <SaveDiscardModal
                      onSave={async () => {
                        await submitForm();
                        setIsOpen(true);
                        changeEditingId(mailingAddressEditingId);
                      }}
                      itemDisplay={editingIdDisplay}
                      saveSubmitting={isSubmitting}
                      onDiscard={() => {
                        resetForm();
                        changeEditingId(mailingAddressEditingId);
                        setIsOpen(true);
                      }}
                    />
                  )}
                </Modal>
              ) : (
                <Button
                  data-testid={`edit-${details.principalInsuredId}-mailing-address`}
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    changeEditingId(mailingAddressEditingId);
                    setIsOpen(true);
                  }}
                >
                  Edit
                </Button>
              )
            }
            editable
            footer={
              editingId === mailingAddressEditingId && (
                <>
                  <ButtonGroup>
                    <Button
                      data-testid={`cancel-contact-info-footer`}
                      variant="secondary"
                      disabled={isSubmitting}
                      onClick={() => {
                        resetForm();
                        changeEditingId(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      isSubmitting={isSubmitting}
                      disabled={isSubmitting || !isValid}
                      data-testid={'save-contact-info-footer'}
                      onClick={async () => {
                        await submitForm();
                        changeEditingId(null);
                      }}
                    >
                      Save Address
                    </Button>
                  </ButtonGroup>
                </>
              )
            }
          >
            <MailingAddress
              key={mailingAddressEditingId}
              hasError={details.mailingAddressOutcomes.isAddressValid === false}
              disabled={!isMailingAddressEditable}
              readOnly={!isMailingAddressEditable}
              tooltip=""
            />
          </AccordionItem>
        )}

        <AccordionItem
          identifier="contactInfoAccordion"
          editable={
            details.drivers[details.principalInsuredId].editable ||
            isEmailEditable
          }
          key={contactInfoEditingId}
          startOpen={editingId === contactInfoEditingId}
          actions={setIsOpen =>
            editingId === contactInfoEditingId ? (
              <Button
                variant="secondary"
                size="sm"
                type="submit"
                onClick={async () => {
                  await submitForm();
                  changeEditingId(null);
                }}
                isSubmitting={isSubmitting}
                disabled={isSubmitting || !isValid}
                data-testid={`${details.principalInsuredId}-save-phone-email-info`}
              >
                Save
              </Button>
            ) : dirty ? (
              <Modal
                variant="normal"
                toggle={openModal => (
                  <Button
                    data-testid={`${details.principalInsuredId}-edit-phone-email`}
                    variant="secondary"
                    size="sm"
                    onClick={openModal}
                  >
                    Edit
                  </Button>
                )}
              >
                {() => (
                  <SaveDiscardModal
                    onSave={async () => {
                      await submitForm();
                      setIsOpen(true);
                      changeEditingId(contactInfoEditingId);
                    }}
                    itemDisplay={editingIdDisplay}
                    saveSubmitting={isSubmitting}
                    onDiscard={() => {
                      resetForm();
                      changeEditingId(contactInfoEditingId);
                      setIsOpen(true);
                    }}
                  />
                )}
              </Modal>
            ) : (
              <Button
                data-testid={`${details.principalInsuredId}-edit-phone-email`}
                variant="secondary"
                size="sm"
                onClick={() => {
                  changeEditingId(contactInfoEditingId);
                  setIsOpen(true);
                }}
              >
                Edit
              </Button>
            )
          }
          header={
            <AccordionHeader
              entity="phone"
              header={formatPhoneAndEmailAccordionHeader(
                details.contactInfo[details.principalInsuredId]
              )}
              dataComplete={phoneAndEmailDataCompleteness['contactInfo']}
            />
          }
          footer={
            editingId === contactInfoEditingId && (
              <>
                <ButtonGroup alignRight>
                  <Button
                    data-testid={`${details.principalInsuredId}-cancel-phone-email-info-footer`}
                    variant="secondary"
                    disabled={isSubmitting}
                    onClick={() => {
                      resetForm();
                      changeEditingId(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    isSubmitting={isSubmitting}
                    disabled={isSubmitting || !isValid}
                    data-testid={`${details.principalInsuredId}-save-phone-email-info-footer`}
                    onClick={async () => {
                      await submitForm();
                      changeEditingId(null);
                    }}
                  >
                    Save Contact Info
                  </Button>
                </ButtonGroup>
              </>
            )
          }
        >
          <ContactInfo
            isEditing={editingId === contactInfoEditingId}
            editable={{
              email: isEmailEditable,
              phoneNumber: details.drivers[details.principalInsuredId].editable,
              phoneType: details.drivers[details.principalInsuredId].editable,
            }}
          />
        </AccordionItem>
      </Accordion>
    </div>
  );
};
